import React from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template34: Página Día 4 - Nara (Kioto)
// Contenido desde page.texts, page.images y page.canvasImages (servidor).
const Template34 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = window.innerWidth <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const naraTitle = page?.texts?.[1]?.content;
	const naraText = page?.texts?.[2]?.content;
	const transporteTitle = page?.texts?.[3]?.content;
	const transporteText = page?.texts?.[4]?.content;
	const todaiTitle = page?.texts?.[5]?.content;
	const todaiText = page?.texts?.[6]?.content;
	const parqueTitle = page?.texts?.[7]?.content;
	const parqueText = page?.texts?.[8]?.content;
	const galletasText = page?.texts?.[9]?.content;
	const extrasText = page?.texts?.[10]?.content;
	const sabiasQueNara = parseSabiasQueItem(page?.texts?.[11]?.content);
	const sabiasQueBuda = parseSabiasQueItem(page?.texts?.[12]?.content);
	const sabiasQueAgujero = parseSabiasQueItem(page?.texts?.[13]?.content);
	const sabiasQueCiervos = parseSabiasQueItem(page?.texts?.[14]?.content);

	const logoImage = getCanvasImageUrl("logo");
	const naraSticker = getCanvasImageUrl("nara-sticker");
	const temploNaraImage = getImageSrc("templo-nara-header");
	const naraBudaImage = getImageSrc("nara-buda");
	const ciervosImage = getImageSrc("ciervos-nara");
	const ciervosSticker = getCanvasImageUrl("ciervos-sticker");

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="text-1" sx={{ position: "relative" }}>
				<Box sx={{ width: "100%" }}>
					<Box sx={{ padding: "10px 12px", position: "relative" }}>
						{naraTitle && (
							<Box
								component="h3"
								sx={{
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									fontSize: "14px",
									fontWeight: 700,
									textTransform: "uppercase",
									flex: 1,
									margin: 0,
								}}
							>
								{naraTitle}
							</Box>
						)}
						{naraText && (
							<Box
								component="p"
								sx={{
									fontSize: "13px",
									color: contentColor,
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									margin: "0 !important",
									marginBottom: "0px !important",
									flex: 1,
								}}
							>
								{naraText}
							</Box>
						)}
						{sabiasQueNara && (
							<Box sx={{ position: "absolute", bottom: "-20px", left: "50%" }}>
								<SabiasQue
									icono={sabiasQueNara.icono}
									texto={sabiasQueNara.texto}
								/>
							</Box>
						)}
					</Box>
					{naraSticker && (
						<Box
							component="img"
							src={naraSticker}
							alt="NaraSticker"
							sx={{
								width: 100,
								position: "absolute",
								bottom: 0,
								right: 0,
							}}
						/>
					)}
				</Box>
				<Box data-block-id="image-0">
					{temploNaraImage && (
					<Box
						component="img"
						src={temploNaraImage}
						alt="Nara"
						sx={{
							width: "100%",
							height: 140,
							objectFit: "cover",
						}}
					/>
				)}
				</Box>
			</Box>

			{(transporteTitle || transporteText) && (
				<Box data-block-id="text-2" sx={{ padding: "12px", paddingBottom: "8px" }}>
					<Box sx={{ position: "relative" }}>
						<Box
							sx={{
								backgroundColor: "#d9e6de",
								border: "3px solid #5ba374",
								borderRadius: "12px",
								padding: "14px 12px",
								fontSize: "12px",
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								color: "#333",
							}}
						>
							{transporteTitle && (
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: "13px",
										fontWeight: 700,
										color: "#5ba374!important",
										textTransform: "uppercase",
									}}
								>
									{transporteTitle}
								</Box>
							)}
							{transporteText && (
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
								>
									{transporteText}
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			)}

			<Box
				data-block-id="text-3"
				sx={{
					display: "flex",
					justifyContent: "space-between",
					flexDirection: "column",
					height: "auto",
				}}
			>
				<Box sx={{ padding: "10px 12px 0px", flex: 1 }}>
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							gap: "16px",
						}}
					>
						<Box
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "16px",
								position: "relative",
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: "20px",
									alignItems: "flex-start",
								}}
							>
								{naraBudaImage && (
									<Box
										component="img"
										src={naraBudaImage}
										alt="Nara"
										sx={{
											width: "40%",
											height: 110,
											borderRadius: "12px",
											objectFit: "cover",
										}}
									/>
								)}
								{sabiasQueBuda && (
									<Box
										sx={{
											position: "absolute",
											top: "10px",
											left: "33%",
											zIndex: 2001,
										}}
									>
										<SabiasQue
											icono={sabiasQueBuda.icono}
											texto={sabiasQueBuda.texto}
										/>
									</Box>
								)}
								{sabiasQueAgujero && (
									<Box
										sx={{
											position: "absolute",
											top: "50px",
											left: "33%",
											zIndex: 2000,
										}}
									>
										<SabiasQue
											icono={sabiasQueAgujero.icono}
											texto={sabiasQueAgujero.texto}
										/>
									</Box>
								)}
								<Box sx={{ width: "60%" }}>
									{todaiTitle && (
										<Box
											component="h3"
											sx={{
												fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
												marginBottom: "4px",
												fontSize: "14px",
												fontWeight: 700,
												textTransform: "uppercase",
												marginTop: "8px",
											}}
										>
											{todaiTitle}
										</Box>
									)}
									{todaiText && (
										<Box
											component="p"
											sx={{
												fontSize: "13px",
												color: contentColor,
												fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
												margin: "0 !important",
												marginBottom: "0px !important",
											}}
										>
											{todaiText}
										</Box>
									)}
								</Box>
							</Box>
						</Box>

						<Box
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "16px",
								position: "relative",
								zIndex: 1000,
							}}
						>
							{ciervosSticker && (
								<>
									{isSmallScreen ? (
										<Box
											component="img"
											src={ciervosSticker}
											alt="CiervosSticker"
											sx={{
												width: 95,
												position: "absolute",
												bottom: "-130px",
												left: "-20px",
											}}
										/>
									) : (
										<Box
											component="img"
											src={ciervosSticker}
											alt="CiervosSticker"
											sx={{
												width: 95,
												position: "absolute",
												bottom: "-52px",
												right: "-50px",
											}}
										/>
									)}
								</>
							)}
							{galletasText && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? "-70px" : "-25px",
										left: isSmallScreen ? "20px" : "auto",
										right: isSmallScreen ? "auto" : "35px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: isSmallScreen
											? "16px 16px 16px 0px"
											: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: "11px",
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: isSmallScreen ? 240 : 280,
										zIndex: 1000,
									}}
								>
									{galletasText}
								</Box>
							)}
							<Box
								sx={{
									display: "flex",
									gap: "20px",
									alignItems: "flex-start",
								}}
							>
								{ciervosImage && (
									<Box
										component="img"
										src={ciervosImage}
										alt="Ciervos"
										sx={{
											width: "40%",
											height: 110,
											borderRadius: "12px",
											objectFit: "cover",
										}}
									/>
								)}
								{sabiasQueCiervos && (
									<Box
										sx={{
											position: "absolute",
											top: "20px",
											left: "34%",
											zIndex: 2000,
										}}
									>
										<SabiasQue
											icono={sabiasQueCiervos.icono}
											texto={sabiasQueCiervos.texto}
										/>
									</Box>
								)}
								<Box sx={{ width: "60%", position: "relative" }}>
									{parqueTitle && (
										<Box
											component="h3"
											sx={{
												fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
												marginBottom: "4px",
												fontSize: "14px",
												fontWeight: 700,
												textTransform: "uppercase",
												marginTop: "8px",
											}}
										>
											{parqueTitle}
										</Box>
									)}
									{parqueText && (
										<Box
											component="p"
											sx={{
												fontSize: "13px",
												color: contentColor,
												fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
												margin: "0 !important",
												marginBottom: "0px !important",
											}}
										>
											{parqueText}
										</Box>
									)}
								</Box>
							</Box>
						</Box>

						{extrasText && (
							<Box
								sx={{
									display: "flex",
									justifyContent: isSmallScreen ? "flex-end" : "flex-start",
								}}
							>
								<Box
									sx={{
										backgroundColor: "#d9e6de",
										padding: "12px",
										borderRadius: "12px",
										display: "flex",
										gap: "16px",
										alignItems: "center",
										position: "relative",
										width: isSmallScreen ? "100%" : "100%",
										marginTop: isSmallScreen ? "100px" : 0,
										marginBottom: isSmallScreen ? "10px" : 0,
									}}
								>
									<Box sx={{ width: "100%" }}>
										<Box
											component="p"
											sx={{
												...p,
												margin: "0 !important",
												marginBottom: "0px !important",
											}}
										>
											{extrasText}
										</Box>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template34.thumbnail = "/images/template-1.png";

export default Template34;
